import React, { useEffect, useRef, useState } from "react";

const formatBytes = (bytes) => {
  if (!bytes && bytes !== 0) return "";
  if (bytes < 1024) return `${bytes} Б`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} КБ`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} МБ`;
};

/**
 * Progress bar for chunked file uploads.
 *
 * Props:
 *   - uploads: array of { name, loaded, total, percent, status: 'pending'|'uploading'|'done'|'error', error? }
 *   - overallPercent: number
 *   - onCancel: () => void              — отменить активные загрузки
 *   - onClose: () => void               — закрыть панель (очистить uploadProgressItems в родителе)
 *   - autoCloseMs: number (default 4000) — автоскрытие после полного успешного завершения
 */
export const UploadProgressBar = ({ uploads = [], overallPercent = 0, onCancel, onClose, autoCloseMs = 4000 }) => {
  const [hidden, setHidden] = useState(false);
  const closeTimerRef = useRef(null);

  // Храним актуальную ссылку на onClose в ref — чтобы таймер всегда вызывал
  // последнюю версию коллбэка без включения onClose в зависимости эффекта.
  const onCloseRef = useRef(onClose);
  useEffect(() => { onCloseRef.current = onClose; });

  const activeCount = uploads.filter((u) => u.status === "uploading" || u.status === "pending").length;
  const doneCount = uploads.filter((u) => u.status === "done").length;
  const errorCount = uploads.filter((u) => u.status === "error").length;
  const allDoneNoErrors = uploads.length > 0 && doneCount === uploads.length && errorCount === 0;

  // Когда приходит новая загрузка — снова показываем панель
  useEffect(() => {
    if (uploads.length > 0) setHidden(false);
  }, [uploads.length]);

  // Автоскрытие через autoCloseMs после полного успешного завершения.
  // onClose намеренно убран из зависимостей: он передаётся как инлайн-функция
  // и меняет ссылку на каждый ре-рендер родителя, что раньше сбрасывало таймер.
  // Актуальная ссылка всегда доступна через onCloseRef.
  useEffect(() => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
    if (allDoneNoErrors && autoCloseMs > 0) {
      closeTimerRef.current = setTimeout(() => {
        setHidden(true);
        if (onCloseRef.current) onCloseRef.current();
      }, autoCloseMs);
    }
    return () => {
      if (closeTimerRef.current) {
        clearTimeout(closeTimerRef.current);
        closeTimerRef.current = null;
      }
    };
  }, [allDoneNoErrors, autoCloseMs]); // onClose намеренно исключён — используется onCloseRef

  if (!uploads.length || hidden) return null;

  const handleCloseClick = () => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
    setHidden(true);
    if (onClose) onClose();
  };

  // Можно закрыть только когда нет активных загрузок
  const canClose = activeCount === 0;

  return (
    <div
      data-testid="upload-progress-bar"
      className="fixed bottom-4 right-4 z-50 w-[360px] max-w-[90vw] bg-white border border-gray-200 shadow-xl rounded-lg p-4"
    >
      <div className="flex items-center justify-between mb-2 gap-2">
        <div className="text-sm font-semibold text-gray-800 flex-1 min-w-0 truncate">
          Загрузка файлов: {doneCount}/{uploads.length}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {onCancel && activeCount > 0 && (
            <button
              data-testid="upload-progress-cancel-btn"
              onClick={onCancel}
              className="text-xs text-red-600 hover:text-red-700 underline"
            >
              Отменить
            </button>
          )}
          {canClose && (
            <button
              data-testid="upload-progress-close-btn"
              onClick={handleCloseClick}
              aria-label="Закрыть панель загрузки"
              className="w-7 h-7 flex items-center justify-center rounded-full text-gray-500 hover:text-gray-800 hover:bg-gray-100 text-lg leading-none"
            >
              ×
            </button>
          )}
        </div>
      </div>

      <div className="w-full bg-gray-100 rounded-full h-2 mb-3 overflow-hidden">
        <div
          data-testid="upload-progress-overall"
          className="h-2 bg-teal-500 transition-all duration-200"
          style={{ width: `${Math.min(100, Math.max(0, overallPercent))}%` }}
        />
      </div>

      <div className="space-y-2 max-h-40 overflow-y-auto pr-1">
        {uploads.map((u, idx) => (
          <div key={idx} data-testid={`upload-progress-item-${idx}`} className="text-xs">
            <div className="flex justify-between items-center">
              <span className="truncate flex-1 mr-2" title={u.name}>
                {u.name}
              </span>
              <span
                className={
                  u.status === "error"
                    ? "text-red-600 font-semibold"
                    : u.status === "done"
                    ? "text-green-600"
                    : "text-gray-600"
                }
              >
                {u.status === "error"
                  ? "Ошибка"
                  : u.status === "done"
                  ? "Готово"
                  : `${u.percent || 0}%`}
              </span>
            </div>
            <div className="w-full bg-gray-100 rounded-full h-1 mt-0.5 overflow-hidden">
              <div
                className={
                  u.status === "error"
                    ? "h-1 bg-red-400"
                    : u.status === "done"
                    ? "h-1 bg-green-500"
                    : "h-1 bg-teal-400"
                }
                style={{ width: `${Math.min(100, Math.max(0, u.percent || 0))}%` }}
              />
            </div>
            {u.total > 0 && u.status !== "error" && (
              <div className="text-gray-500 mt-0.5">
                {formatBytes(u.loaded)} / {formatBytes(u.total)}
              </div>
            )}
            {u.status === "error" && u.error && (
              <div className="text-red-500 mt-0.5 truncate" title={u.error}>
                {u.error}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default UploadProgressBar;
